'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowsOut, X, DownloadSimple } from '@phosphor-icons/react';

interface GeneratedImageOutput {
  success: boolean;
  images?: string[];
  count?: number;
  model?: string;
  prompt?: string;
  textResponse?: string;
  error?: string;
}

/**
 * Renders AI-generated images from the generateImage tool
 * Compact design with fullscreen support
 */
export function GeneratedImageToolOutput({ output }: { output: GeneratedImageOutput }) {
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  if (!output.success) {
    return (
      <div className="p-3 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Image Generation Failed</p>
        <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{output.error}</pre>
      </div>
    );
  }

  if (!output.images || output.images.length === 0) {
    return (
      <div className="p-3 border border-muted rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground">No images were generated.</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full border rounded-lg overflow-hidden bg-card">
        {/* Compact header */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b">
          <span className="text-xs text-muted-foreground truncate max-w-[70%]">
            {output.prompt ? `Generated: ${output.prompt.slice(0, 40)}${output.prompt.length > 40 ? '...' : ''}` : 'AI Generated Image'}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground" />
        </div>

        {/* Image Gallery */}
        <div className={`p-2 ${output.images.length > 1 ? 'grid grid-cols-2 gap-2' : ''}`}>
          {output.images.map((imageDataUrl, index) => (
            <div 
              key={index}
              className="relative group rounded overflow-hidden bg-muted/20 cursor-pointer"
              onClick={() => setFullscreenImage(imageDataUrl)}
            >
              <img 
                src={imageDataUrl}
                alt={`Generated image ${index + 1}${output.prompt ? `: ${output.prompt}` : ''}`}
                className="w-full h-auto object-contain"
                style={{ 
                  maxHeight: output.images!.length > 1 ? '300px' : '500px',
                }}
              />
              {/* Overlay buttons */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <button
                  className="p-2 rounded-full bg-background/90 backdrop-blur-sm border shadow-sm hover:bg-background transition-colors"
                  title="View fullscreen"
                >
                  <ArrowsOut weight="bold" className="size-5" />
                </button>
              </div>
              {/* Download button */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={imageDataUrl}
                  download={`generated-image-${index + 1}.png`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center justify-center size-7 rounded-full bg-background/90 backdrop-blur-sm border shadow-sm hover:bg-background transition-colors"
                  title="Download image"
                >
                  <DownloadSimple weight="bold" className="size-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Text response if any */}
        {output.textResponse && (
          <p className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30">
            {output.textResponse}
          </p>
        )}
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setFullscreenImage(null)}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              onClick={() => setFullscreenImage(null)}
            >
              <X weight="bold" className="size-6" />
            </button>

            {/* Download button */}
            <a
              href={fullscreenImage}
              download="generated-image.png"
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Download image"
            >
              <DownloadSimple weight="bold" className="size-6" />
            </a>

            {/* Full image */}
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={fullscreenImage}
              alt={output.prompt || 'Generated image'}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
